import {useEffect, useRef, useState} from "react";
import {NAIRA} from "../../defaults/utilities";
import {formatCurrency} from "../../helpers/currency";
import {Button, DatePicker, Divider, Form, Input, InputNumber, message, Modal, Popconfirm, Space, Spin} from "antd";
import DataTable from "../common/table";
import {INFLOW, OUTFLOW} from "../../shared/flowType";
import {
    useCreateInflowOutflow, useCreateProjectionHistory,
    useDeleteInflowOutflow, useDeleteProjectionHistory,
    useGetAllInflowOutflow, useGetAllProjectionHistory,
    useUpdateInflowOutflow, useUpdateProjectionHistory
} from "../../hooks/budget/budgetQuery";
import {extractValueFromInputRef} from "../../helpers/dom";
import dayjs from "dayjs";

const dateFormat = 'YYYY-MM-DD';

const NoDataComponent = () => {
    return (
        <h2>
            No data
        </h2>
    )
}

export const ProjectionEntries = ({item}) => {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(false)
    const [show, setShow] = useState(false)

    const getEntries = useGetAllProjectionHistory()

    const INFLOW_COLS = [
        {
            title: 'S/N',
            key: 's/n',
            render: (_, __, idx) => idx + 1
        },
        {
            title: 'Entry date',
            dataIndex: 'dateAsString',
            key: 'dateAsString'
        },
        {
            title: 'Category',
            dataIndex: 'projectionItem',
            key: 'category',
            render: (_, { projectionItem }) => projectionItem?.staffCategory?.title ?? ""
        },
        {
            title: 'Designation',
            dataIndex: 'projectionItem',
            key: 'designation',
            render: (_, { projectionItem }) => projectionItem?.designation?.title ?? ""
        },
        {
            title: 'Level',
            dataIndex: 'projectionItem',
            key: 'level',
            render: (_, { projectionItem }) => projectionItem?.level?.title ?? ""
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (_, { quantity }) => quantity,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, item) => (
                <>
                    <Space>
                        <EditProjectionEntry flowItem={item} onUpdate={() => {}} />
                        <Popconfirm
                            title="Delete this entry"
                            description="Are you sure to delete this ?"
                            onConfirm={() => handleDelete(item)}
                            onCancel={() => null}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button type={"primary"} style={{backgroundColor: "red"}}>Delete</Button>
                        </Popconfirm>
                    </Space>
                </>
            )
        }
    ]

    const deleteEntry = useDeleteProjectionHistory()

    const handleDelete = async (entry) => {
        setLoading(true)
        const d = await deleteEntry(entry.id)
        message.success("Entry deleted")
        setLoading(false)
        await loadItems()
    }
    const loadItems = async () => {
        // load
        setLoading(true)
        const data = await getEntries(item?.id)
        setItems(data)
        setLoading(false)
    }

    useEffect(() => {
        if(item && show){
            // load
            loadItems()
        }
    }, [item, show]);

    return (
        <>
            <Space>
                <Button type="primary" onClick={() => setShow(!show)}>Entries</Button>
            </Space>
            <Modal centered footer={null} open={show} onCancel={() => setShow(false)} width="70%">
                <div></div>
                <Divider style={{
                    marginTop: '1.5em',
                    marginBottom: '5px'
                }}/>
                <Spin spinning={loading}>
                    <DataTable data={items} cols={INFLOW_COLS} locale={{
                        emptyText: <NoDataComponent/>
                    }}></DataTable>
                </Spin>
            </Modal>
        </>
    )
}


export const NewProjectionEntry = ({projectionItem, onUpdate}) => {
    const [show, setShow] = useState(false)
    const [processing, setProcessing] = useState(false)

    const [date, setDate] = useState(null)

    const quantityRef = useRef(null)

    const createFlow = useCreateProjectionHistory()

    const handleSubmit = async () => {
        setProcessing(true)
        const d = await createFlow({
            quantity: quantityRef.current.value,
            entryDate: date,
            projectionItemId: projectionItem?.id ?? null
        })
        message.success("Entry added")
        setProcessing(false)
        onUpdate()
        setShow(false)
    }


    return (
        <div>
            <Button onClick={() => setShow(true)} type="primary" style={{backgroundColor: "green"}}>
                Add
            </Button>
            <Modal centered title="Add new entry" footer={null} width={600} open={show} onCancel={() => setShow(false)}>
                <Form style={{
                    marginTop: "1.5em"
                }}>
                    <Form.Item name="quantity">
                        <InputNumber stringMode style={{width: "100%"}} ref={quantityRef} placeholder="Quantity" size="large"></InputNumber>
                    </Form.Item>
                    <Form.Item name="date">
                        <DatePicker format={dateFormat} style={{ width: '100%' }} onChange={(_, str) => {
                            setDate(str)
                        }} />
                    </Form.Item>
                    <Form.Item
                    >
                        <div style={{textAlign: "right", display: 'flex', justifyContent: 'flex-end'}}>
                            <Button loading={processing} style={{backgroundColor:"green", textAlign: "right"}} size="large" type="primary" htmlType="submit" onClick={handleSubmit}>
                                Add entry
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}



export const EditProjectionEntry = ({flowItem, onUpdate}) => {
    const [show, setShow] = useState(false)
    const [processing, setProcessing] = useState(false)

    const [date, setDate] = useState(flowItem?.dateAsString ?? null)

    const quantityRef = useRef(null)

    const updateFlow = useUpdateProjectionHistory()

    const handleSubmit = async () => {
        // setProcessing(true)
        const d = await updateFlow(flowItem?.id, {
            quantity: quantityRef.current.value,
            entryDate: date,
            projectionItemId: flowItem?.projectionItem?.id ?? null
        })
        message.success("Entry updated")
        setProcessing(false)
        onUpdate()
        setShow(false)
    }


    return (
        <div>
            {
                // budgetItem?.budgetItem?.budgetHead?.budgetCategory?.flow === INFLOW.value &&
                <Button onClick={() => setShow(true)} type="primary">
                    Update
                </Button>
            }
            <Modal centered title="Add new entry" footer={null} width={600} open={show} onCancel={() => setShow(false)}>
                <Form initialValues={{...flowItem, date: dayjs(flowItem?.dateAsString ?? date, dateFormat)}} style={{
                    marginTop: "1.5em"
                }}>
                    <Form.Item name="quantity">
                        <InputNumber stringMode style={{width: "100%"}} ref={quantityRef} placeholder="Quantity" size="large"></InputNumber>
                    </Form.Item>
                    <Form.Item name="date">
                        <DatePicker format={dateFormat} style={{ width: '100%' }} onChange={(_, str) => {
                            setDate(str)
                        }} />
                    </Form.Item>
                    <Form.Item
                    >
                        <div style={{textAlign: "right", display: 'flex', justifyContent: 'flex-end'}}>
                            <Button loading={processing} style={{backgroundColor:"green", textAlign: "right"}} size="large" type="primary" htmlType="submit" onClick={handleSubmit}>
                                Update entry
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}
